import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import SuccessPopup from '../shared/SuccessPopup';

const HouseholdInformation = () => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [saveFeedback, setSaveFeedback] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);

  // Auto-fill mother/father info when "Same as Primary" is checked/unchecked
  useEffect(() => {
    if (formData.mother_same_as_primary) {
      setFormData(prev => ({
        ...prev,
        mother_guardian_first_name: prev.primary_parent_first_name || '',
        mother_guardian_last_name: prev.primary_parent_last_name || '',
        mother_home_phone: prev.primary_parent_home_phone || '',
        mother_cell_phone: prev.primary_parent_cell_phone || '',
        mother_employer_phone: prev.primary_parent_employer_phone || '',
        mother_email: prev.primary_parent_email || ''
      }));
    }
  }, [formData.mother_same_as_primary, formData.primary_parent_first_name, formData.primary_parent_last_name, formData.primary_parent_home_phone, formData.primary_parent_cell_phone, formData.primary_parent_employer_phone, formData.primary_parent_email]);

  useEffect(() => {
    if (formData.father_same_as_primary) {
      setFormData(prev => ({
        ...prev,
        father_guardian_first_name: prev.primary_parent_first_name || '',
        father_guardian_last_name: prev.primary_parent_last_name || '',
        father_home_phone: prev.primary_parent_home_phone || '',
        father_cell_phone: prev.primary_parent_cell_phone || '',
        father_employer_phone: prev.primary_parent_employer_phone || '',
        father_email: prev.primary_parent_email || ''
      }));
    }
  }, [formData.father_same_as_primary, formData.primary_parent_first_name, formData.primary_parent_last_name, formData.primary_parent_home_phone, formData.primary_parent_cell_phone, formData.primary_parent_employer_phone, formData.primary_parent_email]);

  // Auto-fill mailing address from home address when checked
  useEffect(() => {
    if (!formData.mailing_same_as_primary_residence) return;
    setFormData((prev) => {
      const next = {
        ...prev,
        primary_mailing_address_street: prev.home_street_address || '',
        primary_mailing_address_city: prev.home_city || '',
        primary_mailing_address_county: prev.home_county || '',
        primary_mailing_address_state: prev.home_state || '',
        primary_mailing_address_zip: prev.home_zip_code || ''
      };
      if (
        next.primary_mailing_address_street === prev.primary_mailing_address_street &&
        next.primary_mailing_address_city === prev.primary_mailing_address_city &&
        next.primary_mailing_address_county === prev.primary_mailing_address_county &&
        next.primary_mailing_address_state === prev.primary_mailing_address_state &&
        next.primary_mailing_address_zip === prev.primary_mailing_address_zip
      ) {
        return prev;
      }
      return next;
    });
  }, [
    formData.mailing_same_as_primary_residence,
    formData.home_street_address,
    formData.home_city,
    formData.home_county,
    formData.home_state,
    formData.home_zip_code
  ]);

  // Keep a canonical "primary household phone" field based on selected primary contact type
  useEffect(() => {
    const selectedType = formData.primary_contact_phone_type;
    let selectedPhone = '';
    if (selectedType === 'cell') {
      selectedPhone = formData.primary_parent_cell_phone || '';
    } else if (selectedType === 'home') {
      selectedPhone = formData.primary_parent_home_phone || '';
    }
    if (selectedPhone === (formData.primary_household_phone || '')) return;
    setFormData((prev) => ({ ...prev, primary_household_phone: selectedPhone }));
  }, [
    formData.primary_contact_phone_type,
    formData.primary_parent_cell_phone,
    formData.primary_parent_home_phone,
    formData.primary_household_phone
  ]);

  useEffect(() => {
    if (user && !dataLoaded) {
      fetchHouseholdData();
    }
  }, [user, dataLoaded]);

  const fetchHouseholdData = async () => {
    try {
      const response = await axios.get('/api/form-submissions/household-information');
      setFormData(response.data || {});
      setDataLoaded(true);
    } catch (error) {
      console.error('Failed to fetch household information:', error);
      setFormData({});
      setDataLoaded(true);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'primary_contact_home_checkbox') {
      setFormData((prev) => ({
        ...prev,
        primary_contact_phone_type: checked ? 'home' : (prev.primary_contact_phone_type === 'home' ? '' : prev.primary_contact_phone_type)
      }));
      return;
    }

    if (name === 'primary_contact_cell_checkbox') {
      setFormData((prev) => ({
        ...prev,
        primary_contact_phone_type: checked ? 'cell' : (prev.primary_contact_phone_type === 'cell' ? '' : prev.primary_contact_phone_type)
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaveFeedback(null);
    setLoading(true);

    try {
      await axios.post('/api/form-submissions/household-information', {
        household_data: formData
      });
      setSaveFeedback({
        variant: 'success',
        title: 'Success!',
        message: 'Household information saved successfully!'
      });
    } catch (error) {
      console.error('Failed to save household information:', error);
      setSaveFeedback({
        variant: 'error',
        title: 'Save failed',
        message: error.response?.data?.error || 'Failed to save household information'
      });
    } finally {
      setLoading(false);
    }
  };

  if (!dataLoaded) {
    return <div>Loading household information...</div>;
  }

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto', padding: '2rem' }}>
      <h2>Household Information</h2>
      <p style={{ marginBottom: '2rem', color: '#666' }}>
        This information will be automatically used to pre-fill forms when enrolling your students.
        Please keep it up to date.
      </p>

      <SuccessPopup
        show={!!saveFeedback}
        onClose={() => setSaveFeedback(null)}
        variant={saveFeedback?.variant || 'success'}
        title={saveFeedback?.title}
        message={saveFeedback?.message || ''}
        buttonText="OK"
        autoCloseDelay={saveFeedback?.variant === 'error' ? 0 : 5000}
      />

      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ marginBottom: '1rem', color: '#2c3e50' }}>Primary Parent Information</h3>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Primary Parent First Name
            </label>
            <input
              type="text"
              name="primary_parent_first_name"
              value={formData.primary_parent_first_name || ''}
              onChange={handleChange}
              style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Primary Parent Last Name
            </label>
            <input
              type="text"
              name="primary_parent_last_name"
              value={formData.primary_parent_last_name || ''}
              onChange={handleChange}
              style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Primary Parent Home Phone
            </label>
            <input
              type="tel"
              name="primary_parent_home_phone"
              value={formData.primary_parent_home_phone || ''}
              onChange={handleChange}
              style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
            />
            <label style={{ display: 'inline-block', marginTop: '0.5rem', fontSize: '0.95rem' }}>
              <input
                type="checkbox"
                name="primary_contact_home_checkbox"
                checked={formData.primary_contact_phone_type === 'home'}
                onChange={handleChange}
                style={{ marginRight: '0.5rem' }}
              />
              Primary Contact
            </label>
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Primary Parent Cell Phone
            </label>
            <input
              type="tel"
              name="primary_parent_cell_phone"
              value={formData.primary_parent_cell_phone || ''}
              onChange={handleChange}
              style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
            />
            <label style={{ display: 'inline-block', marginTop: '0.5rem', fontSize: '0.95rem' }}>
              <input
                type="checkbox"
                name="primary_contact_cell_checkbox"
                checked={formData.primary_contact_phone_type === 'cell'}
                onChange={handleChange}
                style={{ marginRight: '0.5rem' }}
              />
              Primary Contact
            </label>
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Primary Parent Employer Phone
            </label>
            <input
              type="tel"
              name="primary_parent_employer_phone"
              value={formData.primary_parent_employer_phone || ''}
              onChange={handleChange}
              style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Primary Parent Email
            </label>
            <input
              type="email"
              name="primary_parent_email"
              value={formData.primary_parent_email || ''}
              onChange={handleChange}
              style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
            />
          </div>
        </div>

        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ marginBottom: '1rem', color: '#2c3e50' }}>Mother/Guardian Information</h3>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'inline-block', marginRight: '1rem', fontWeight: '500' }}>
              <input
                type="checkbox"
                name="mother_same_as_primary"
                checked={formData.mother_same_as_primary || false}
                onChange={(e) => handleChange({ target: { name: e.target.name, value: e.target.checked } })}
                style={{ marginRight: '0.5rem' }}
              />
              Same as Primary Parent
            </label>
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Mother/Guardian First Name
            </label>
            <input
              type="text"
              name="mother_guardian_first_name"
              value={formData.mother_guardian_first_name || ''}
              onChange={handleChange}
              style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Mother/Guardian Last Name
            </label>
            <input
              type="text"
              name="mother_guardian_last_name"
              value={formData.mother_guardian_last_name || ''}
              onChange={handleChange}
              style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Mother/Guardian Home Phone
            </label>
            <input
              type="tel"
              name="mother_home_phone"
              value={formData.mother_home_phone || ''}
              onChange={handleChange}
              style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Mother/Guardian Cell Phone
            </label>
            <input
              type="tel"
              name="mother_cell_phone"
              value={formData.mother_cell_phone || ''}
              onChange={handleChange}
              style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Mother/Guardian Employer Phone
            </label>
            <input
              type="tel"
              name="mother_employer_phone"
              value={formData.mother_employer_phone || ''}
              onChange={handleChange}
              style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Mother/Guardian Email
            </label>
            <input
              type="email"
              name="mother_email"
              value={formData.mother_email || ''}
              onChange={handleChange}
              style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
            />
          </div>
        </div>

        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ marginBottom: '1rem', color: '#2c3e50' }}>Father/Guardian Information</h3>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'inline-block', marginRight: '1rem', fontWeight: '500' }}>
              <input
                type="checkbox"
                name="father_same_as_primary"
                checked={formData.father_same_as_primary || false}
                onChange={(e) => handleChange({ target: { name: e.target.name, value: e.target.checked } })}
                style={{ marginRight: '0.5rem' }}
              />
              Same as Primary Parent
            </label>
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Father/Guardian First Name
            </label>
            <input
              type="text"
              name="father_guardian_first_name"
              value={formData.father_guardian_first_name || ''}
              onChange={handleChange}
              style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Father/Guardian Last Name
            </label>
            <input
              type="text"
              name="father_guardian_last_name"
              value={formData.father_guardian_last_name || ''}
              onChange={handleChange}
              style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Father/Guardian Home Phone
            </label>
            <input
              type="tel"
              name="father_home_phone"
              value={formData.father_home_phone || ''}
              onChange={handleChange}
              style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Father/Guardian Cell Phone
            </label>
            <input
              type="tel"
              name="father_cell_phone"
              value={formData.father_cell_phone || ''}
              onChange={handleChange}
              style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Father/Guardian Employer Phone
            </label>
            <input
              type="tel"
              name="father_employer_phone"
              value={formData.father_employer_phone || ''}
              onChange={handleChange}
              style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Father/Guardian Email
            </label>
            <input
              type="email"
              name="father_email"
              value={formData.father_email || ''}
              onChange={handleChange}
              style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
            />
          </div>
        </div>

        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ marginBottom: '1rem', color: '#2c3e50' }}>Home Address</h3>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Street Address
            </label>
            <input
              type="text"
              name="home_street_address"
              value={formData.home_street_address || ''}
              onChange={handleChange}
              style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
            />
          </div>

          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                City
              </label>
              <input
                type="text"
                name="home_city"
                value={formData.home_city || ''}
                onChange={handleChange}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                County
              </label>
              <input
                type="text"
                name="home_county"
                value={formData.home_county || ''}
                onChange={handleChange}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                State
              </label>
              <input
                type="text"
                name="home_state"
                value={formData.home_state || ''}
                onChange={handleChange}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Zip Code
              </label>
              <input
                type="text"
                name="home_zip_code"
                value={formData.home_zip_code || ''}
                onChange={handleChange}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
              />
            </div>
          </div>
        </div>

        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ marginBottom: '1rem', color: '#2c3e50' }}>Mailing Address</h3>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'inline-block', marginRight: '1rem', fontWeight: '500' }}>
              <input
                type="checkbox"
                name="mailing_same_as_primary_residence"
                checked={formData.mailing_same_as_primary_residence || false}
                onChange={handleChange}
                style={{ marginRight: '0.5rem' }}
              />
              Same as primary residence address
            </label>
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Mailing Street Address
            </label>
            <input
              type="text"
              name="primary_mailing_address_street"
              value={formData.primary_mailing_address_street || ''}
              onChange={handleChange}
              disabled={!!formData.mailing_same_as_primary_residence}
              style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
            />
          </div>

          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Mailing City
              </label>
              <input
                type="text"
                name="primary_mailing_address_city"
                value={formData.primary_mailing_address_city || ''}
                onChange={handleChange}
                disabled={!!formData.mailing_same_as_primary_residence}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Mailing County
              </label>
              <input
                type="text"
                name="primary_mailing_address_county"
                value={formData.primary_mailing_address_county || ''}
                onChange={handleChange}
                disabled={!!formData.mailing_same_as_primary_residence}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Mailing State
              </label>
              <input
                type="text"
                name="primary_mailing_address_state"
                value={formData.primary_mailing_address_state || ''}
                onChange={handleChange}
                disabled={!!formData.mailing_same_as_primary_residence}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Mailing Zip Code
              </label>
              <input
                type="text"
                name="primary_mailing_address_zip"
                value={formData.primary_mailing_address_zip || ''}
                onChange={handleChange}
                disabled={!!formData.mailing_same_as_primary_residence}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
              />
            </div>
          </div>
        </div>

        <div style={{ marginBottom: '2rem' }}>
          <label style={{ display: 'inline-block', marginBottom: '1rem', fontWeight: '600', color: '#2c3e50' }}>
            <input
              type="checkbox"
              name="has_additional_household_information"
              checked={formData.has_additional_household_information || false}
              onChange={handleChange}
              style={{ marginRight: '0.5rem' }}
            />
            Add additional household information
          </label>

          {formData.has_additional_household_information && (
            <div style={{ padding: '1rem', border: '1px solid #eee', borderRadius: '4px' }}>
              <h3 style={{ marginBottom: '1rem', color: '#2c3e50' }}>Secondary Residence (Optional)</h3>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Secondary Residence Street Address
                </label>
                <input
                  type="text"
                  name="secondary_residence_street_address"
                  value={formData.secondary_residence_street_address || ''}
                  onChange={handleChange}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                />
              </div>

              <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>City</label>
                  <input
                    type="text"
                    name="secondary_residence_city"
                    value={formData.secondary_residence_city || ''}
                    onChange={handleChange}
                    style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>County</label>
                  <input
                    type="text"
                    name="secondary_residence_county"
                    value={formData.secondary_residence_county || ''}
                    onChange={handleChange}
                    style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>State</label>
                  <input
                    type="text"
                    name="secondary_residence_state"
                    value={formData.secondary_residence_state || ''}
                    onChange={handleChange}
                    style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Zip Code</label>
                  <input
                    type="text"
                    name="secondary_residence_zip"
                    value={formData.secondary_residence_zip || ''}
                    onChange={handleChange}
                    style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                  />
                </div>
              </div>

              <h3 style={{ margin: '1.5rem 0 1rem', color: '#2c3e50' }}>Secondary Parent/Guardian 1</h3>
              <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>First Name</label>
                  <input
                    type="text"
                    name="additional_parent_first_name"
                    value={formData.additional_parent_first_name || ''}
                    onChange={handleChange}
                    style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Last Name</label>
                  <input
                    type="text"
                    name="additional_parent_last_name"
                    value={formData.additional_parent_last_name || ''}
                    onChange={handleChange}
                    style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Home Phone</label>
                  <input
                    type="tel"
                    name="additional_parent_home_phone"
                    value={formData.additional_parent_home_phone || ''}
                    onChange={handleChange}
                    style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Cell Phone</label>
                  <input
                    type="tel"
                    name="additional_parent_phone"
                    value={formData.additional_parent_phone || ''}
                    onChange={handleChange}
                    style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                  />
                </div>
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Email</label>
                <input
                  type="email"
                  name="additional_parent_email"
                  value={formData.additional_parent_email || ''}
                  onChange={handleChange}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                />
              </div>

              <h3 style={{ margin: '1.5rem 0 1rem', color: '#2c3e50' }}>Secondary Parent/Guardian 2 (Optional)</h3>
              <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>First Name</label>
                  <input
                    type="text"
                    name="secondary_parent_2_first_name"
                    value={formData.secondary_parent_2_first_name || ''}
                    onChange={handleChange}
                    style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Last Name</label>
                  <input
                    type="text"
                    name="secondary_parent_2_last_name"
                    value={formData.secondary_parent_2_last_name || ''}
                    onChange={handleChange}
                    style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Home Phone</label>
                  <input
                    type="tel"
                    name="secondary_parent_2_home_phone"
                    value={formData.secondary_parent_2_home_phone || ''}
                    onChange={handleChange}
                    style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Cell Phone</label>
                  <input
                    type="tel"
                    name="secondary_parent_2_cell_phone"
                    value={formData.secondary_parent_2_cell_phone || ''}
                    onChange={handleChange}
                    style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                  />
                </div>
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Email</label>
                <input
                  type="email"
                  name="secondary_parent_2_email"
                  value={formData.secondary_parent_2_email || ''}
                  onChange={handleChange}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                />
              </div>
            </div>
          )}
        </div>

        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ marginBottom: '1rem', color: '#2c3e50' }}>Emergency Contacts</h3>

          <div style={{ marginBottom: '2rem', padding: '1rem', border: '1px solid #eee', borderRadius: '4px' }}>
            <h4 style={{ marginTop: '0', marginBottom: '1rem', color: '#34495e' }}>Emergency Contact 1</h4>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Name
              </label>
              <input
                type="text"
                name="emergency_contact_1_name"
                value={formData.emergency_contact_1_name || ''}
                onChange={handleChange}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Home Phone
              </label>
              <input
                type="tel"
                name="emergency_contact_1_home_phone"
                value={formData.emergency_contact_1_home_phone || ''}
                onChange={handleChange}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Cell/Work Phone
              </label>
              <input
                type="tel"
                name="emergency_contact_1_cell_work_phone"
                value={formData.emergency_contact_1_cell_work_phone || ''}
                onChange={handleChange}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
              />
            </div>
          </div>

          <div style={{ marginBottom: '2rem', padding: '1rem', border: '1px solid #eee', borderRadius: '4px' }}>
            <h4 style={{ marginTop: '0', marginBottom: '1rem', color: '#34495e' }}>Emergency Contact 2</h4>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Name
              </label>
              <input
                type="text"
                name="emergency_contact_2_name"
                value={formData.emergency_contact_2_name || ''}
                onChange={handleChange}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Home Phone
              </label>
              <input
                type="tel"
                name="emergency_contact_2_home_phone"
                value={formData.emergency_contact_2_home_phone || ''}
                onChange={handleChange}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Cell/Work Phone
              </label>
              <input
                type="tel"
                name="emergency_contact_2_cell_work_phone"
                value={formData.emergency_contact_2_cell_work_phone || ''}
                onChange={handleChange}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
              />
            </div>
          </div>
        </div>

        <div style={{ marginTop: '2rem', paddingTop: '1rem', borderTop: '1px solid #eee' }}>
          <button
            type="submit"
            disabled={loading}
            style={{
              backgroundColor: '#27ae60',
              color: 'white',
              border: 'none',
              padding: '0.75rem 2rem',
              borderRadius: '4px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '1rem',
              fontWeight: '500'
            }}
          >
            {loading ? 'Saving...' : 'Save Household Information'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default HouseholdInformation;
